import { Camera, MapPin, Upload } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { typography } from '../../../styles/typography';
import { Button, Card } from '../../ui';
import Input from '../../ui/input';
import { vendorApi } from '../../Vender/vendorApi.js';
import { stockInApi } from './stockInApi.js';

const StockOnboarding = () => {
  const [formData, setFormData] = useState({
    stockName: '',
    measuringQuantity: '',
    reorderThreshold: '',
    reorderQuantity: '',
    vendorName: '',
    currentQuantity: '',
    siteId: '',
  });

  const [sites, setSites] = useState([]);
  const [loadingSites, setLoadingSites] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [loadingVendors, setLoadingVendors] = useState(false);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState({ success: '', error: '' });
  const [isSaving, setIsSaving] = useState(false);
  const [imageData, setImageData] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [hasImage, setHasImage] = useState(false);
  const fileInputRef = useRef(null);

  // Camera functionality state
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);
  const [cameraError, setCameraError] = useState('');
  const [isCapturing, setIsCapturing] = useState(false);
  const [showCameraPreview, setShowCameraPreview] = useState(false);

  // Camera refs
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: '' }));
    setStatus({ success: '', error: '' });
  };

  // Load sites on component mount
  useEffect(() => {
    const loadSites = async () => {
      setLoadingSites(true);
      try {
        const sitesData = await stockInApi.getSites();
        setSites(sitesData || []);
      } catch (err) {
        console.error('Error loading sites:', err);
        setSites([]);
      } finally {
        setLoadingSites(false);
      }
    };

    loadSites();
  }, []);

  // Load vendors on component mount
  useEffect(() => {
    const loadVendors = async () => {
      setLoadingVendors(true);
      try {
        const result = await vendorApi.listVendors({
          limit: 100,
          status: 'active',
        });

        if (result.success && result.vendors) {
          const vendorNames = result.vendors
            .map(vendor => vendor.vendor_name)
            .filter(name => name && name.trim().length > 0)
            .sort();
          
          console.log('Loaded vendors:', vendorNames);
          console.log('Vendor count:', vendorNames.length);
          setVendors(vendorNames);
          if (vendorNames.length === 0) {
            console.warn('No vendors found in API response');
          }
        } else {
          console.warn('No vendors found or API error:', result);
          setVendors([]);
        }
      } catch (err) {
        console.error('Error loading vendors:', err);
        setVendors([]);
      } finally {
        setLoadingVendors(false);
      }
    };

    loadVendors();
  }, []);


  // Cleanup image preview URL on unmount
  useEffect(() => {
    return () => {
      if (imagePreview && imagePreview.startsWith('blob:')) {
        URL.revokeObjectURL(imagePreview);
      }
    };
  }, [imagePreview]);

  // Cleanup camera on component unmount
  useEffect(() => {
    return () => {
      if (cameraStream) {
        cameraStream.getTracks().forEach(track => track.stop());
      }
    };
  }, [cameraStream]);

  // Auto-hide success message after 5 seconds
  useEffect(() => {
    if (status.success) {
      const timer = setTimeout(() => {
        setStatus((prev) => ({ ...prev, success: '' }));
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [status.success]);

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Check file size before processing (5MB limit)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      setErrors((prev) => ({
        ...prev,
        image: `Image file is too large (${(file.size / (1024 * 1024)).toFixed(2)} MB). Maximum allowed size is 5MB.`,
      }));
      return;
    }

    // Check file type
    if (!file.type.startsWith('image/')) {
      setErrors((prev) => ({
        ...prev,
        image: 'Please select a valid image file.',
      }));
      return;
    }

    try {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.image;
        return newErrors;
      });

      const reader = new FileReader();
      reader.onload = (e) => {
        const base64 = e.target.result.split(',')[1];
        setImageData(base64);
        setImagePreview(e.target.result);
        setHasImage(true);
      };

      reader.onerror = () => {
        setErrors((prev) => ({
          ...prev,
          image: 'Failed to read image file. Please try again.',
        }));
      };

      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error processing image:', error);
      setErrors((prev) => ({
        ...prev,
        image: 'Failed to process image. Please try again.',
      }));
    }
  };

  const handleRemoveImage = () => {
    if (imagePreview && imagePreview.startsWith('blob:')) {
      URL.revokeObjectURL(imagePreview);
    }
    setImageData(null);
    setImagePreview(null);
    setHasImage(false);
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors.image;
      return newErrors;
    });
  };

  // Check if camera is available
  const checkCameraAvailability = async () => {
    try {
      // Check if getUserMedia is supported
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        return {
          available: false,
          error: 'Camera not supported in this browser',
        };
      }

      // Check if any video input devices exist
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter(
        device => device.kind === 'videoinput'
      );

      if (videoDevices.length === 0) {
        return { available: false, error: 'No camera found on this device' };
      }

      return { available: true, devices: videoDevices };
    } catch (err) {
      console.error('Error checking camera availability:', err);
      return { available: false, error: 'Unable to check camera availability' };
    }
  };

  // Start camera
  const startCamera = async () => {
    try {
      setCameraError('');
      setIsCameraActive(true);
      setShowCameraPreview(true);

      // First check camera availability
      const cameraCheck = await checkCameraAvailability();
      if (!cameraCheck.available) {
        throw new Error(cameraCheck.error);
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'user', // Use front camera by default
          width: { ideal: 640 },
          height: { ideal: 480 },
        },
      });

      setCameraStream(stream);

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
    } catch (err) {
      console.error('Camera access error:', err);

      // More specific error messages
      let errorMessage = 'Unable to access camera. Please check permissions.';

      if (err.name === 'NotAllowedError') {
        errorMessage = 'Camera access denied. Please allow camera permissions.';
      } else if (err.name === 'NotFoundError') {
        errorMessage = 'No camera found on this device.';
      } else if (err.name === 'NotReadableError') {
        errorMessage = 'Camera is already in use by another application.';
      } else if (err.name === 'OverconstrainedError') {
        errorMessage = 'Camera does not meet the required specifications.';
      } else if (err.message.includes('not supported')) {
        errorMessage = 'Camera not supported in this browser.';
      } else if (err.message.includes('No camera found')) {
        errorMessage = 'No camera found on this device.';
      }

      setCameraError(errorMessage);
      setIsCameraActive(false);
      setShowCameraPreview(false);
    }
  };

  // Stop camera
  const stopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
    setIsCameraActive(false);
    setShowCameraPreview(false);
    setCameraError('');
  };

  // Camera capture
  const takePhoto = async () => {
    if (!videoRef.current || !canvasRef.current) return;

    setIsCapturing(true);

    try {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');

      // Set canvas dimensions to match video
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      // Draw video frame to canvas
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(video, 0, 0);

      // Convert to base64
      const photoDataUrl = canvas.toDataURL('image/jpeg', 0.9);
      const base64 = photoDataUrl.split(',')[1];

      // Set the captured image
      setImageData(base64);
      setImagePreview(photoDataUrl);
      setHasImage(true);

      // Stop camera after taking photo
      stopCamera();
    } catch (err) {
      console.error('Error capturing photo:', err);
      setErrors((prev) => ({
        ...prev,
        image: 'Failed to capture photo. Please try again.',
      }));
    } finally {
      setIsCapturing(false);
    }
  };

  const handleCameraCapture = () => {
    if (isCameraActive) {
      takePhoto();
    } else {
      startCamera();
    }
  };

  const validateForm = () => {
    const nextErrors = {};
    if (!formData.stockName.trim()) {
      nextErrors.stockName = 'Stock name is required';
    }

    if (!formData.measuringQuantity.trim()) {
      nextErrors.measuringQuantity = 'Measuring quantity is required';
    }

    if (!formData.siteId) {
      nextErrors.siteId = 'Site selection is required';
    }

    if (!formData.reorderThreshold) {
      nextErrors.reorderThreshold = 'Enter a threshold';
    }

    if (!formData.reorderQuantity) {
      nextErrors.reorderQuantity = 'Enter a reorder value';
    }

    if (!formData.vendorName) {
      nextErrors.vendorName = 'Vendor selection is required';
    }

    if (!formData.currentQuantity) {
      nextErrors.currentQuantity = 'Current quantity is required';
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setStatus({ success: '', error: '' });

    if (!validateForm()) {
      return;
    }

    setIsSaving(true);
    try {
      // Get selected site details
      const selectedSite = sites.find(s => (s.site_id || s.id) === formData.siteId);
      const siteName = selectedSite?.site_name || selectedSite?.name || '';

      // Prepare API payload according to API spec
      const apiPayload = {
        item_name: formData.stockName.trim(),
        unit_of_measure: formData.measuringQuantity.trim(),
        site_id: formData.siteId,
        ...(siteName ? { site_name: siteName } : {}),
        ...(formData.vendorName ? { vendor_name: formData.vendorName } : {}),
        current_quantity: parseFloat(formData.currentQuantity) || 0,
        reorder_threshold: parseFloat(formData.reorderThreshold) || 0,
        reorder_quantity: parseFloat(formData.reorderQuantity) || 0,
      };

      console.log('Submitting stock item payload:', apiPayload);

      // Create stock item first
      const result = await stockInApi.createStockItem(apiPayload);

      if (!result.success) {
        setStatus({
          success: '',
          error: result.message || result.error || 'Failed to create stock item. Please try again.',
        });
        setIsSaving(false);
        return;
      }

      const createdItem = result.item;
      let imageKey = null;
      let imageContentType = null;

      // If image was uploaded, handle image upload via presign
      if (imageData && createdItem?.stock_item_id) {
        try {
          // Get file type from base64 data
          const contentType = imagePreview?.match(/data:([^;]+);/)?.[1] || 'image/jpeg';
          
          // Get presigned URL
          const presignResult = await stockInApi.presignStockItemImageUpload(
            createdItem.stock_item_id,
            contentType
          );

          if (presignResult.success) {
            // Convert base64 to blob
            const base64Response = await fetch(imagePreview);
            const blob = await base64Response.blob();

            // Upload to S3 using presigned POST
            const uploadFormData = new FormData();
            Object.keys(presignResult.presigned_post.fields).forEach(key => {
              uploadFormData.append(key, presignResult.presigned_post.fields[key]);
            });
            uploadFormData.append('file', blob);

            const uploadResponse = await fetch(presignResult.presigned_post.url, {
              method: 'POST',
              body: uploadFormData,
            });

            if (uploadResponse.ok) {
              imageKey = presignResult.object_key;
              imageContentType = presignResult.content_type;

              // Update stock item with image key
              await stockInApi.updateStockItem(createdItem.stock_item_id, {
                image_key: imageKey,
                image_content_type: imageContentType,
              });
            }
          }
        } catch (imageError) {
          console.error('Error uploading image:', imageError);
          // Don't fail the entire submission if image upload fails
          setStatus({ 
            success: 'Stock item created successfully, but image upload failed.', 
            error: '' 
          });
          // Reset form
          setFormData({
            stockName: '',
            measuringQuantity: '',
            reorderThreshold: '',
            reorderQuantity: '',
            vendorName: '',
            currentQuantity: '',
            siteId: '',
          });
      setImageData(null);
          setImagePreview(null);
          setHasImage(false);
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
          setIsSaving(false);
          return;
        }
      }

      setStatus({ success: 'Stock item created successfully!', error: '' });
      
      // Reset form after successful submission
      setFormData({
        stockName: '',
        measuringQuantity: '',
        reorderThreshold: '',
        reorderQuantity: '',
        vendorName: '',
        currentQuantity: '',
        siteId: '',
      });
      setImageData(null);
      setImagePreview(null);
      setHasImage(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      console.error('Error submitting stock item:', error);
      setStatus({
        success: '',
        error: error.message || 'Unable to save stock data. Please try again later.',
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    
      <div className="max-w-5xl mx-auto h-full">
        <div className="mb-8 text-center">
          <h3 className={typography.h3} style={{ color: '#000000' }}>
            Capture Stock Thresholds
          </h3>
          <p className={typography.p} style={{ color: '#000000' }}>
            Add baseline values so alerts can fire whenever inventory dips.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Profile Image Section */}
          <Card.Glassmorphism className="p-6">
            <div className="text-center">
              <h2
                className={typography.h2}
                style={{ color: '#000000', marginBottom: '1.5rem' }}
              >
                Profile Image
              </h2>

              <div className="flex flex-col items-center gap-4">
                {/* Image Preview */}
                <div className="relative">
                  {imagePreview ? (
                    <div className="w-32 h-32 rounded-full border-4 border-gray-300 overflow-hidden">
                      <img
                        src={imagePreview}
                        alt="Profile preview"
                        className="w-full h-full object-cover"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setImagePreview(null);
                          setImageData(null);
                          setHasImage(false);
                        }}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-2 hover:bg-red-600 transition-colors"
                      >
                        ×
                      </button>
                    </div>
                  ) : (
                    <div className="w-32 h-32 rounded-full border-4 border-dashed border-gray-300 flex items-center justify-center bg-gray-50">
                      <Camera className="w-12 h-12 text-gray-400" />
                    </div>
                  )}
                </div>

                {/* Upload Buttons */}
                <div className="flex gap-4">
                  <Button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700"
                    style={{ color: '#ffffff' }}
                  >
                    <Upload className="w-4 h-4" style={{ color: '#ffffff' }} />
                    Upload Image
                  </Button>
                  <Button
                    type="button"
                    onClick={handleCameraCapture}
                    className={`flex items-center gap-2 ${
                      isCameraActive
                        ? 'bg-red-600 hover:bg-red-700'
                        : 'bg-green-600 hover:bg-green-700'
                    }`}
                    style={{ color: '#ffffff' }}
                  >
                    <Camera className="w-4 h-4" style={{ color: '#ffffff' }} />
                    Take Photo
                  </Button>
                </div>

                {/* Hidden Canvas for Photo Capture */}
                <canvas
                  ref={canvasRef}
                  className="hidden"
                  width="640"
                  height="480"
                />

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                />
                {errors.image && (
                  <p className="text-sm text-red-500">{errors.image}</p>
                )}
              </div>

              {/* Camera Preview - Positioned directly below the upload buttons section */}
              {showCameraPreview && (
                <div className="w-full mt-6">
                  <div className="p-4 border border-gray-300 rounded-lg bg-gray-50">
                    <div className="text-center mb-3">
                      <h3 className="text-lg font-medium text-gray-800 mb-2">
                        Camera Preview
                      </h3>
                      {cameraError && (
                        <p className="text-red-600 text-sm mb-2">
                          {cameraError}
                        </p>
                      )}
                    </div>

                    <div className="flex flex-col items-center gap-4">
                      <div className="relative">
                        <video
                          ref={videoRef}
                          autoPlay
                          playsInline
                          muted
                          className="w-80 h-60 rounded-lg border border-gray-300 bg-black"
                        />

                        <div className="flex justify-center gap-3 mt-3">
                          <Button
                            type="button"
                            onClick={takePhoto}
                            disabled={isCapturing}
                            className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 px-4 py-2"
                          >
                            {isCapturing ? 'Capturing...' : 'Capture Photo'}
                          </Button>

                          <Button
                            type="button"
                            onClick={stopCamera}
                            className="bg-gray-600 hover:bg-gray-700 px-4 py-2"
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </Card.Glassmorphism>

        <Card.Gradient className="p-8">
          <div className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Stock Name
                </label>
                <Input
                  value={formData.stockName}
                  onChange={(e) => handleInputChange('stockName', e.target.value)}
                  placeholder="E.g. Diesel, Gloves, CCTV Cameras"
                />
                {errors.stockName && (
                  <p className="mt-2 text-sm text-red-500">{errors.stockName}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Measuring Quantity
                </label>
                <Input
                  value={formData.measuringQuantity}
                  onChange={(e) =>
                    handleInputChange('measuringQuantity', e.target.value)
                  }
                  placeholder="Liters, Units, Boxes..."
                />
                {errors.measuringQuantity && (
                  <p className="mt-2 text-sm text-red-500">
                    {errors.measuringQuantity}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <MapPin className="inline w-4 h-4 mr-1" />
                  Site
                </label>
                <select
                  value={formData.siteId}
                  onChange={(e) => handleInputChange('siteId', e.target.value)}
                  disabled={loadingSites}
                  className={`w-full h-12 rounded-2xl border bg-white px-4 text-sm focus:outline-none focus:ring-2 disabled:bg-gray-100 disabled:cursor-not-allowed ${
                    errors.siteId
                      ? 'border-red-500 focus:ring-red-500'
                      : 'border-gray-200 focus:ring-green-500'
                  }`}
                >
                  <option value="" disabled hidden>
                    {loadingSites ? 'Loading sites...' : 'Select site'}
                  </option>
                  {Array.isArray(sites) && [...sites]
                    .sort((a, b) => {
            const nameA = (a.site_name || a.name || 'Unknown Site').toLowerCase();
            const nameB = (b.site_name || b.name || 'Unknown Site').toLowerCase();
                      return nameA.localeCompare(nameB);
                    })
                    .map((site) => (
                      <option key={site.site_id || site.id} value={site.site_id || site.id}>
                        {site.site_name || site.name || 'Unknown Site'}
                      </option>
                    ))}
                </select>
                {errors.siteId && (
                  <p className="mt-2 text-sm text-red-500">{errors.siteId}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Reorder Threshold
                </label>
                <Input
                  type="number"
                  min="0"
                  value={formData.reorderThreshold}
                  onChange={(e) =>
                    handleInputChange('reorderThreshold', e.target.value)
                  }
                  placeholder="When should alerts trigger?"
                />
                {errors.reorderThreshold && (
                  <p className="mt-2 text-sm text-red-500">
                    {errors.reorderThreshold}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Reorder Quantity
                </label>
                <Input
                  type="number"
                  min="0"
                  value={formData.reorderQuantity}
                  onChange={(e) =>
                    handleInputChange('reorderQuantity', e.target.value)
                  }
                  placeholder="How much to reorder?"
                />
                {errors.reorderQuantity && (
                  <p className="mt-2 text-sm text-red-500">
                    {errors.reorderQuantity}
                  </p>
                )}
              </div>

              <div>
              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Vendor Name
                </label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400 pointer-events-none" />
                  <select
                    value={formData.vendorName || ''}
                    onChange={(e) => handleInputChange('vendorName', e.target.value)}
                    disabled={loadingVendors}
                    className={`w-full h-12 rounded-2xl border bg-white pl-10 pr-4 text-sm focus:outline-none focus:ring-2 disabled:bg-gray-100 disabled:cursor-not-allowed ${
                      errors.vendorName
                        ? 'border-red-500 focus:ring-red-500'
                        : 'border-gray-200 focus:ring-green-500'
                    }`}
                    required
                  >
                    <option value="" disabled>
                      {loadingVendors ? 'Loading vendors...' : 'Select vendor'}
                    </option>
                    {vendors.map((vendor) => (
                      <option key={vendor} value={vendor}>
                        {vendor}
                      </option>
                    ))}
                  </select>
                </div>
                {vendors.length === 0 && !loadingVendors && (
                  <p className="mt-2 text-sm text-gray-500">
                    No vendors available. Please onboard vendors first.
                  </p>
                )}
                {errors.vendorName && (
                  <p className="mt-2 text-sm text-red-500">
                    {errors.vendorName}
                  </p>
                )}
              </div>
              </div>

              

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Current Quantity
                </label>
                <Input
                  type="number"
                  min="0"
                  value={formData.currentQuantity}
                  onChange={(e) =>
                    handleInputChange('currentQuantity', e.target.value)
                  }
                  placeholder="What is in stock now?"
                />
                {errors.currentQuantity && (
                  <p className="mt-2 text-sm text-red-500">
                    {errors.currentQuantity}
                  </p>
                )}
              </div>
            </div>

            {status.error && (
              <p className="text-sm text-red-600">{status.error}</p>
            )}
            {status.success && (
              <p className="text-sm text-emerald-600">{status.success}</p>
            )}

            <div className="flex justify-end">
              <button
                type="submit"
                disabled={isSaving}
                className="px-8 py-3 rounded-xl bg-green-600 text-white font-semibold shadow-lg hover:bg-green-700 disabled:opacity-60 transition"
              >
                {isSaving ? 'Saving...' : 'Save Stock'}
              </button>
            </div>
          </div>
        </Card.Gradient>
        </form>
      </div>
    
  );
};

export default StockOnboarding;
